import React, { useMemo } from "react";
import { Chip } from "@/components/ui-bits";

export const OUTCOMES = [
    { key: "pending", label: "Pending", short: "pending", tone: "" },
    { key: "pass", label: "Pass", short: "pass", tone: "" },
    { key: "r_and_r", label: "R&R", short: "R&R", tone: "strong" },
    { key: "offer", label: "Offer", short: "offer", tone: "strong" },
    { key: "acquired", label: "Acquired", short: "acquired", tone: "strong" },
    { key: "no_response", label: "No response", short: "no reply", tone: "" },
];

const KEY_TO = Object.fromEntries(OUTCOMES.map((o) => [o.key, o]));

export function OutcomeChip({ value }) {
    const o = KEY_TO[value] || KEY_TO.pending;
    return <Chip tone={o.tone}>{o.label}</Chip>;
}

/**
 * Compact dropdown chip. On change, calls onChange(newKey).
 */
export function OutcomeSelect({ value, onChange, testId }) {
    return (
        <select
            className="input-flat text-xs font-mono uppercase tracking-widest !py-1 !px-2 !pr-6 min-w-[110px]"
            value={value || "pending"}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
        >
            {OUTCOMES.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
            ))}
        </select>
    );
}

export function RoundSummary({ rounds, currentRound, className = "" }) {
    // Previous-rounds strip: derived by filter+sort on every render, so memoize
    // — RoundSummary re-renders on every outcome update in ProjectDetail.
    const previousRounds = useMemo(
        () => (rounds || [])
            .filter((r) => r.round !== currentRound)
            .sort((a, b) => b.round - a.round),
        [rounds, currentRound],
    );
    if (!rounds || rounds.length === 0) return null;
    const cur = rounds.find((r) => r.round === currentRound) || rounds[0];
    if (!cur || cur.sent === 0) {
        return (
            <div className={"paper border border-border rounded-sm p-4 " + className} data-testid="round-summary">
                <div className="label-mono mb-2">Round {currentRound}</div>
                <div className="text-sm text-muted-foreground">
                    No editor sends yet in this round.
                </div>
            </div>
        );
    }
    const stats = [
        { label: "Sent", value: cur.sent, big: true },
        { label: "Pending", value: cur.pending },
        { label: "Passes", value: cur.pass },
        { label: "R&R", value: cur.r_and_r },
        { label: "Offers", value: cur.offer, strong: cur.offer > 0 },
        cur.acquired > 0 ? { label: "Acquired", value: cur.acquired, strong: true } : null,
    ].filter(Boolean);
    return (
        <div className={"paper border border-border rounded-sm " + className} data-testid="round-summary">
            <div className="flex flex-wrap items-stretch">
                <div className="p-4 pr-6 border-r border-border">
                    <div className="label-mono">Current</div>
                    <div className="font-serif text-2xl leading-none mt-1">Round {currentRound}</div>
                </div>
                {stats.map((s, i) => (
                    <div key={s.label} className={"p-4 " + (i < stats.length - 1 ? "border-r border-border/60" : "")}>
                        <div className="label-mono">{s.label}</div>
                        <div className={"font-serif leading-none mt-1 " + (s.big ? "text-2xl" : "text-xl") + (s.strong ? " text-[#1B362F]" : "")}>
                            {s.value}
                        </div>
                    </div>
                ))}
            </div>
            {rounds.length > 1 && (
                <div className="border-t border-border px-4 py-3 flex items-center gap-4 flex-wrap text-xs">
                    <span className="label-mono">Previous</span>
                    {previousRounds.map((r) => (
                        <span key={r.round} className="text-muted-foreground">
                            R{r.round}: <span className="text-foreground">{r.sent}</span> sent
                            {r.offer > 0 && <span className="text-[#1B362F] font-medium"> · {r.offer} offer{r.offer > 1 ? "s" : ""}</span>}
                            {r.acquired > 0 && <span className="text-[#1B362F] font-medium"> · {r.acquired} acquired</span>}
                            {r.pass > 0 && <span> · {r.pass} pass{r.pass > 1 ? "es" : ""}</span>}
                            {r.r_and_r > 0 && <span> · {r.r_and_r} R&R</span>}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
